/**
 * FormWidget — T3.13 catalog entry for contentType 'form'.
 *
 * Renders a contact-form widget using the FormWidgetContent schema
 * from @website-builder/schemas. In edit mode, shows the form
 * fields as they'll appear to visitors plus an "Edit fields" panel.
 * In preview mode (low zoom), shows the form non-interactively.
 *
 * Submission: POST /api/v1/sites/:siteId/forms/:formId (T3.22).
 * The honeypot field is always rendered (hidden by CSS, not display:none
 * so bots fill it). See T3.23 for the full honeypot client.
 *
 * The editor canvas renders it with Duro's form kit (`Form` + `Field`); the
 * published page renders its own static markup (renderPage's renderForm), so
 * nothing here has to mirror that HTML.
 */
import { useMemo, useRef, useState } from "react";
import { Schema } from "effect";
import { css, html } from "react-strict-dom";
import { Button, Checkbox, Field, Input, Textarea } from "@duro-app/ui";
import { Form } from "@duro-app/ui/form";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { spacing } from "@duro-app/tokens/tokens/spacing.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";
import type { WidgetProps } from "./types.js";
import type { FormWidgetContent } from "@website-builder/schemas";
import { CHECKBOX_ON } from "./formSubmit.js";

type FormField = FormWidgetContent["fields"][number];
type FormValues = Record<string, string>;

const styles = css.create({
  thanks: {
    padding: spacing.md,
    textAlign: "center",
    color: colors.success,
    fontSize: typography.fontSizeSm,
  },
  body: {
    padding: spacing.ms,
    display: "flex",
    flexDirection: "column",
    gap: spacing.ms,
  },
  // Off-screen, not display:none — bots fill it, people never reach it.
  honeypot: {
    position: "absolute",
    left: "-100vw",
    width: 0,
    height: 0,
    overflow: "hidden",
    opacity: 0,
    pointerEvents: "none",
  },
  empty: {
    color: colors.textMuted,
    fontSize: typeScale.fontSize2,
    margin: 0,
  },
  required: {
    color: colors.error,
  },
  error: {
    color: colors.error,
    fontSize: typeScale.fontSize2,
    margin: 0,
  },
});

export function FormWidget({ itemId, contentProps }: WidgetProps) {
  const preview = contentProps?.["preview"] as boolean | undefined;
  const siteId = contentProps?.["siteId"] as string | undefined;
  const fields = useMemo(
    () => (contentProps?.["fields"] ?? []) as FormField[],
    [contentProps],
  );
  const submitLabel = (contentProps?.["submitButtonLabel"] as string) ?? "Send";

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);

  // Every field submits a string, as the FormData post did: text as typed, a
  // checkbox "on" when ticked and "" otherwise. Required-ness stays with the
  // browser (`required` on each control), as before.
  const schema = useMemo(
    () =>
      Schema.Struct(
        Object.fromEntries(fields.map((f) => [f.name, Schema.String])),
      ) as unknown as Schema.Schema<FormValues>,
    [fields],
  );
  const defaultValues = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.name, ""])) as FormValues,
    [fields],
  );

  const handleSubmit = async (values: FormValues) => {
    if (!siteId) return;
    // Honeypot: if the hidden "website" field is filled, silently drop.
    if (honeypotRef.current?.value) return;

    const payload: Record<string, string> = {};
    for (const field of fields) {
      payload[field.name] = String(values[field.name] ?? "");
    }

    setSubmitting(true);
    setErrorMsg(null);
    try {
      const r = await fetch(`/api/v1/sites/${siteId}/forms/${itemId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin",
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(
          (b as { message?: string }).message ?? `HTTP ${r.status}`,
        );
      }
      setSubmitted(true);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <html.div style={styles.thanks}>
        ✓ Thank you! Your message has been sent.
      </html.div>
    );
  }

  return (
    <Form<FormValues>
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      {({ setValue }) => (
        <html.div style={styles.body}>
          {/* Honeypot — hidden from real users, bots fill it. Outside the
              form's values (read through its ref); the inert, aria-hidden box
              keeps it out of the tab order and the accessibility tree. */}
          <html.div inert aria-hidden={true} style={styles.honeypot}>
            <Input
              ref={honeypotRef}
              type="text"
              name="website"
              autoComplete="off"
              aria-label="Website"
            />
          </html.div>

          {fields.length === 0 && (
            <html.p style={styles.empty}>
              No fields configured. Edit this form widget to add fields.
            </html.p>
          )}

          {fields.map((field) =>
            field.type === "checkbox" ? (
              <Checkbox
                key={field.name}
                name={field.name}
                disabled={preview || submitting}
                onChange={(e) =>
                  setValue(field.name, e.target.checked ? CHECKBOX_ON : "")
                }
              >
                {field.label}
                {field.required && (
                  <html.span style={styles.required}> *</html.span>
                )}
              </Checkbox>
            ) : (
              <Field.Root
                key={field.name}
                name={field.name}
                required={field.required}
              >
                <Field.Label>
                  {field.label}
                  {field.required && (
                    <html.span style={styles.required}> *</html.span>
                  )}
                </Field.Label>
                {field.type === "textarea" ? (
                  <Textarea
                    placeholder={field.placeholder}
                    required={field.required}
                    rows={(field as { rows?: number }).rows ?? 3}
                    disabled={preview || submitting}
                  />
                ) : (
                  <Input
                    type={field.type}
                    placeholder={field.placeholder}
                    required={field.required}
                    disabled={preview || submitting}
                  />
                )}
              </Field.Root>
            ),
          )}

          {fields.length > 0 && (
            // Wrapper keeps the button content-sized (left-aligned) instead of
            // stretching in the flex column.
            <html.div>
              <Button
                type="submit"
                variant="primary"
                disabled={preview || submitting}
              >
                {submitting ? "Sending…" : submitLabel}
              </Button>
            </html.div>
          )}

          {errorMsg && <html.p style={styles.error}>{errorMsg}</html.p>}
        </html.div>
      )}
    </Form>
  );
}
